import { defineCollection, type RebaseContext } from "@rebasepro/cms-types";
import { policy } from "@rebasepro/types";
import tiersCollection from "./tiers.js";
import { evaluateMember, type RuleTier, type RuleTransaction, type TierKind } from "../loyalty-rules.js";

type Row = Record<string, unknown>;

/** A relation as written (an id) or read back (an id, or an expanded `{ id }`). */
function relationId(value: unknown): string | null {
    const id = value && typeof value === "object" ? (value as Row).id : value;
    return id === null || id === undefined || id === "" ? null : String(id);
}

function asDate(value: unknown): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A field of the customer as it will be after this save: the payload's when
 * it carries the field (an explicit null included), else the row's. Rebase
 * hands `beforeSave` the raw payload, not the merged entity.
 */
function current(values: Row, previous: Row, ...keys: string[]): unknown {
    for (const key of keys) if (key in values && values[key] !== undefined) return values[key];
    for (const key of keys) if (previous[key] !== undefined) return previous[key];
    return null;
}

const sameDate = (a: unknown, b: unknown) => (asDate(a)?.getTime() ?? null) === (asDate(b)?.getTime() ?? null);

/**
 * The programme's rules (config/loyalty-rules.ts), applied to this save: the
 * points of the last 12 months, the level and how long it is kept, the
 * second-purchase offer, what keeping the level takes, and when to look
 * again. Read in the save's own database transaction, so a sale recorded a
 * moment ago (its transaction's callback is what saves the customer) counts.
 *
 * Every save of a member runs it — a sale, a return, Family & Friends given
 * or taken off, the daily review touching them — and the fields it sets are
 * never written by anything else.
 */
async function applyRules(values: Row, previousValues: Row | undefined, context: unknown, id: string | number | undefined) {
    const data = (context as { data?: { collection?: (slug: string) => { find: (q: Row) => Promise<{ data?: Row[] } | undefined> } } } | undefined)?.data;
    if (!data || typeof data.collection !== "function") return;
    const previous = previousValues ?? {};
    const tierRows = (await data.collection("tiers").find({ limit: 100 }))?.data ?? [];
    if (tierRows.length === 0) return;
    const tierIds = new Map(tierRows.map((row) => [String(row.id), row.id]));
    const tiers: RuleTier[] = tierRows.map((row) => ({
        id: String(row.id),
        name: String(row.name ?? ""),
        kind: (["points", "manual", "offer"].includes(String(row.kind)) ? row.kind : "points") as TierKind,
        threshold: Number(row.points_threshold ?? 0),
        discount: Number(row.discount_percentage ?? 0),
    }));
    const txRows = id === undefined || id === null ? [] : ((await data.collection("transactions").find({
        where: { customer: ["==", id] },
        limit: 10000,
    }))?.data ?? []);
    const transactions: RuleTransaction[] = txRows
        .map((row) => ({
            type: row.type === "return" ? "return" as const : "purchase" as const,
            source: String(row.source ?? ""),
            amount: Math.abs(Number(row.gross_amount ?? 0)),
            occurredAt: asDate(row.occurred_at ?? row.occurredAt) ?? asDate(row.created_at ?? row.createdAt) ?? new Date(),
            externalOrderId: (row.external_order_id ?? row.externalOrderId ?? null) as string | null,
        }))
        .filter((t) => Number.isFinite(t.amount));

    const now = new Date();
    const evaluation = evaluateMember({
        tierId: relationId(current(values, previous, "current_tier", "currentTierId")),
        reachedAt: asDate(current(values, previous, "tier_reached_at")),
        heldUntil: asDate(current(values, previous, "tier_held_until")),
        manualTierId: relationId(current(values, previous, "manual_tier", "manualTierId")),
        warnedFor: asDate(current(values, previous, "level_warning_sent_for")),
    }, transactions, tiers, now);

    const iso = (date: Date | null) => (date ? date.toISOString() : null);
    values.points = evaluation.points;
    values.current_tier = evaluation.tierId === null ? null : tierIds.get(evaluation.tierId) ?? evaluation.tierId;
    values.tier_reached_at = iso(evaluation.reachedAt);
    values.tier_held_until = iso(evaluation.heldUntil);
    values.offer_until = iso(evaluation.offerUntil);
    values.first_purchase_at = iso(evaluation.firstPurchaseAt);
    values.purchases_count = evaluation.purchases;
    values.next_expiry_at = iso(evaluation.nextExpiryAt);
    values.next_expiry_points = evaluation.nextExpiryPoints;
    values.points_to_keep_level = evaluation.pointsToKeepLevel;
    values.review_at = iso(evaluation.reviewAt);
    values.evaluated_at = now.toISOString();
}

/**
 * Call one of the backend functions for a customer, and say what happened.
 *
 * These were four buttons in a hand-written `CustomerLookupView` that also had
 * its own search box, its own result list and its own detail card — a second
 * customer screen maintained in parallel with the real one, which is why it
 * still rendered `{ id, values }` entities three SDK versions after that shape
 * was dropped. The actions were the only part of it the collection could not
 * already do. They live here now and the view is gone.
 *
 * Declared in the config package, which is plain TypeScript that the backend
 * also loads: hence Lucide icon *names* rather than elements, and
 * `context.client` rather than an imported frontend client. Nothing here drags
 * React into a process that only wants the schema.
 *
 * Only the body is shared. Each action stays a literal in `entityActions` so
 * that its `entity.values` is the collection's own inferred shape and a typo in
 * a field name is a compile error rather than a button that is always disabled
 * — a helper returning `EntityAction<M>` cannot be, since `M` is what
 * `defineCollection` is still inferring from the same object.
 */
async function invokeCustomerFunction(
    { name, fn, path, method = "POST", done }: {
        name: string;
        fn: string;
        /** Sub-path under the function, given the customer's id. */
        path: (id: string) => string;
        method?: "GET" | "POST";
        /** What the snackbar says on success. */
        done: string;
    },
    { id, context, onCollectionChange }: {
        id: string;
        context: RebaseContext;
        onCollectionChange?: () => void;
    }
): Promise<void> {
    const functions = context.client.functions;
    if (!functions) {
        context.snackbarController?.open({
            type: "error",
            message: `${name}: este cliente no puede llamar a las funciones del servidor.`
        });
        return;
    }
    try {
        await functions.invoke(fn, undefined, { method, path: path(id) });
        // The functions write to the customer row — the tier, the pass serial —
        // so the row on screen is stale the moment they return.
        onCollectionChange?.();
        context.snackbarController?.open({ type: "success", message: done });
    } catch (error) {
        context.snackbarController?.open({
            type: "error",
            message: `${name}: ${error instanceof Error ? error.message : String(error)}`
        });
    }
}

const customersCollection = defineCollection({
    name: "Customers",
    singularName: "Customer",
    slug: "customers",
    table: "customers",
    description: "Central customer registry synced from Odoo (master) and Shopify. Tracks lifetime spending and current loyalty tier.",
    history: true,
    // Full-text search, because the default substring search cannot do the two
    // things this list is searched for. Measured on a fixture of Spanish names
    // before it was chosen (see the notes on each setting):
    //
    //   query                  before          after
    //   "sebastian melendez"   nothing         the one customer
    //   "munoz" / "garcia"     nothing         Muñoz / García
    //   "seb"                  both Sebastians both Sebastians
    //
    // The whole typed string used to be matched against each column on its own,
    // so a name split across `first_name` and `last_name` could not be found at
    // all. That half is now fixed framework-side; the accents are not, and never
    // will be without this block.
    search: {
        // Not "spanish". These are names, emails and QR serials, not prose: a
        // Spanish stemmer would fold real surnames onto invented roots and drop
        // any customer actually called `De` or `Los`. "simple" keeps each token
        // as typed.
        language: "simple",
        // The reason this block exists. Postgres stems `Muñoz` and `munoz` to
        // different lexemes, so without folding, staff typing on a POS keyboard
        // — which is most of the time — miss every customer whose name carries
        // an accent, and that is most of them.
        unaccent: true,
        // Exact lexeme matching alone would lose prefix search: `seb` is not the
        // token `sebastian`, so the search box would go dead the moment someone
        // typed half a name. Trigram similarity carries the prefix across, and
        // covers a misspelt surname on the way.
        fuzzy: true,
        // Measured, not guessed. At 0.3 (the default) `sebastian melendez`
        // dragged in every Ana; at 0.6 a full email address matched a second
        // customer; at 0.9 `sebas` matched nothing. 0.75 was exactly right on
        // every probe, and being above 0.6 it also gets the index-backed `<%`
        // operator rather than a scan.
        fuzzyThreshold: 0.75,
        fields: [
            // Weighted: a name is what the customer is looked up by, and the
            // contact details are how the right one is confirmed.
            { path: "first_name", weight: "A" },
            { path: "last_name", weight: "A" },
            "email",
            "phone",
            // The QR payload scanned at the POS. The lookup view matched it
            // exactly already; this makes a partial one findable too.
            //
            // The two external ids are deliberately NOT here, and adding one is
            // not a free edit. `odoo_partner_id` cannot be indexed at all — the
            // index is text and that column is a number — and adding
            // `shopify_customer_id` changes the generated expression, which boot
            // refuses to rebuild on its own: dropping and re-adding a STORED
            // column rewrites the table under an ACCESS EXCLUSIVE lock, so it
            // wants a scheduled `DROP COLUMN "search_vector"` first. Until then,
            // looking a customer up by either id is the column filter, not the
            // search box.
            "wallet_pass_serial"
        ]
    },
    securityRules: [
        { operation: "select", access: "public" },
        { operations: ["insert", "update", "delete"], roles: ["admin"] },
        // Ver la nota en transactions.ts: los webhooks crean y actualizan
        // clientes sin usuario autenticado.
        { operations: ["insert", "update", "delete"], condition: policy.serverContext() }
    ],
    callbacks: {
        beforeSave: async ({ values, previousValues, context, id }) => {
            // Auto-calculate net points from gross and returns.
            // Rebase passes the raw update payload here — NOT the merged entity —
            // so on a partial update `values` only contains the changed fields.
            // A transaction bumps just lifetime_gross_spent OR lifetime_returns,
            // so we must fall back to previousValues for the field not in this
            // patch; otherwise the missing one reads as 0 and net points (and the
            // resulting tier) get computed wrong. See tests/customers-callback.test.ts.
            const gross = (values.lifetime_gross_spent ?? previousValues?.lifetime_gross_spent ?? 0) as number;
            const returns = (values.lifetime_returns ?? previousValues?.lifetime_returns ?? 0) as number;
            values.lifetime_net_points = Math.max(0, gross - returns);
            await applyRules(values as Row, previousValues as Row | undefined, context, id);
            return values;
        },
        // Rebase 0.9 renamed the `entityId` callback prop to `id`; aliased here
        // so the body below (and the sync calls) stay unchanged.
        afterSave: async ({ values, id: entityId, previousValues, context }) => {
            // The level was set by beforeSave (config/loyalty-rules.ts); here
            // it is only compared with what it was, logged when it moved, and
            // pushed. Reads return the relation's derived foreign key
            // (`currentTierId` since 0.14), writes the property; accept both.
            const valuesRow = values as Record<string, unknown>;
            const prevRow = (previousValues ?? {}) as Record<string, unknown>;
            const tierNow = relationId(valuesRow.current_tier ?? valuesRow.currentTierId);
            const tierBefore = relationId(prevRow.current_tier ?? prevRow.currentTierId);
            const currentTierId = tierBefore;
            const tierChanged = tierNow !== tierBefore;
            const netPoints = Number(valuesRow.points ?? values.lifetime_net_points ?? 0);
            // The loyalty id is the barcode Odoo keeps on the contact. Issuing a
            // pass sets it without touching the tier, so until 2026-09-21 a new
            // card never reached Odoo until the customer's first tier change.
            const loyaltyIdChanged = Boolean(values.wallet_pass_serial)
                && values.wallet_pass_serial !== previousValues?.wallet_pass_serial;
            // The second-purchase offer opening or closing changes the price
            // the member pays (its own pricelist and Shopify tag), not the level.
            const offerChanged = !sameDate(valuesRow.offer_until, prevRow.offer_until);
            const manualChanged = relationId(valuesRow.manual_tier ?? valuesRow.manualTierId)
                !== relationId(prevRow.manual_tier ?? prevRow.manualTierId);

            if (tierChanged) {
                const returned = Number(values.lifetime_returns ?? 0) > Number(previousValues?.lifetime_returns ?? 0);
                const bought = Number(values.lifetime_gross_spent ?? 0) > Number(previousValues?.lifetime_gross_spent ?? 0);
                // Neither bought nor returned: points expired or the level's
                // 12 months ended (the daily review), or the tiers themselves
                // were changed (a manual decision).
                const pointsFell = Number(valuesRow.points ?? 0) < Number(prevRow.points ?? 0);
                const heldUntil = asDate(prevRow.tier_held_until);
                const levelEnded = heldUntil !== null && heldUntil.getTime() <= Date.now();
                const reason = manualChanged ? "manual_override"
                    : returned ? "return"
                    : bought || tierBefore === null ? "purchase"
                    : pointsFell || levelEnded ? "expiry" : "manual_override";
                await context.data.collection("tier_changes").create({
                    customer: entityId,
                    previous_tier: currentTierId ?? null,
                    new_tier: tierNow,
                    reason,
                    net_points_at_change: netPoints
                });
            }

            const pointsChanged = Number(valuesRow.points ?? 0) !== Number(prevRow.points ?? 0)
                || Number(values.lifetime_net_points ?? 0) !== Number(previousValues?.lifetime_net_points ?? 0);
            // What the card and the Odoo contact show besides points and level.
            // `level_warning_sent_for` is written by the daily review when the
            // 30-day warning goes: the card's level line then reads the
            // warning, and its Apple change message is the lock-screen notice.
            const statusChanged = ["tier_held_until", "next_expiry_at", "next_expiry_points", "points_to_keep_level", "level_warning_sent_for"]
                .some((key) => key.endsWith("_at") || key.endsWith("_until")
                    ? !sameDate(valuesRow[key], prevRow[key])
                    : Number(valuesRow[key] ?? 0) !== Number(prevRow[key] ?? 0));

            // This callback runs inside the save's database transaction, and
            // the pushes below read on their own connection: started now, one
            // can read the customer before this save commits and push the
            // state from before it (seen end to end: a promotion's own push
            // carried the old tier). `since` is this save's timestamp; each
            // push waits until the customer shows it.
            const savedAt = valuesRow.updated_at ?? valuesRow.updatedAt ?? valuesRow.created_at ?? valuesRow.createdAt;
            const savedDate = savedAt ? new Date(savedAt as string) : null;
            const sinceIso = savedDate && !Number.isNaN(savedDate.getTime()) ? savedDate.toISOString() : undefined;

            // ── Wallet cards ────────────────────────────────────
            // Google changes a saved card only when told. Whatever the card
            // shows — points, tier, name, membership — goes to it, in this
            // process and fire-and-forget like the Odoo push below. A member
            // who never opened their Google link has no card there, and the
            // push stops at that without calling Google.
            const shownOnCard = ["first_name", "last_name", "is_active"]
                .some((key) => valuesRow[key] !== undefined && valuesRow[key] !== prevRow[key]);
            // A tier written outright — "Recalcular nivel", or the update just
            // above — arrives already equal to the qualifying tier, so
            // `tierChanged` misses it. Compared as strings: the id comes back
            // as a number from one side and may be written as a string.
            const writtenTier = valuesRow.current_tier ?? valuesRow.currentTierId;
            const tierWritten = writtenTier !== undefined
                && String(writtenTier ?? "") !== String(prevRow.current_tier ?? prevRow.currentTierId ?? "");
            if (pointsChanged || tierChanged || tierWritten || loyaltyIdChanged || shownOnCard || offerChanged || statusChanged) {
                type CardPush = (customerId: string, since?: string) => Promise<string>;
                const walletPush = (globalThis as Record<symbol, unknown>)[Symbol.for("unfeigned-loyalty.wallet-push")] as
                    Record<string, CardPush | undefined> | undefined;
                walletPush?.google?.(String(entityId), sinceIso).catch((err) => {
                    // Log but don't throw — the card catches up on the next change
                    console.error(`[Google Wallet] Failed to update the card of customer ${entityId}:`, err);
                });
                // Apple: the iPhones holding the card are told to fetch it again.
                walletPush?.apple?.(String(entityId), sinceIso).catch((err) => {
                    console.error(`[Apple Wallet] Failed to tell the iPhones of customer ${entityId}:`, err);
                });
            }

            // ── Outbound Shopify Sync ───────────────────────────
            // The web shop's tier discount is a Shopify automatic discount for
            // the customers tagged with the tier's `shopify_tag`, so the tag
            // follows the member: pushed when the tier changes or is written,
            // when they leave or come back, and when they are first linked to
            // a Shopify customer. A member not linked yet — new, or with a new
            // email — is looked up in Shopify by email, and linking them is a
            // save that lands back here and pushes the tag.
            //
            // In this process and fire-and-forget, like the pushes around it:
            // over HTTP each counted against the service key's rate limit.
            if (process.env.SHOPIFY_STORE_DOMAIN) {
                const shopifyId = values.shopify_customer_id ?? null;
                const linkedNow = Boolean(shopifyId)
                    && String(shopifyId) !== String(prevRow.shopify_customer_id ?? "");
                const membershipChanged = valuesRow.is_active !== undefined
                    && prevRow.is_active !== undefined && valuesRow.is_active !== prevRow.is_active;
                const emailChanged = Boolean(values.email) && values.email !== prevRow.email;
                const shopifyPath = shopifyId
                    ? ((tierChanged || tierWritten || membershipChanged || linkedNow || offerChanged) ? "sync-tags" : null)
                    : ((emailChanged || membershipChanged) && values.is_active !== false ? "link" : null);
                if (shopifyPath) {
                    type ShopifyPush = (customerId: string, since?: string) => Promise<{ status: number; body: unknown }>;
                    const shopifyPush = (globalThis as Record<symbol, unknown>)[Symbol.for("unfeigned-loyalty.shopify-push")] as
                        Record<string, ShopifyPush | undefined> | undefined;
                    const runShopify = shopifyPush?.[shopifyPath];
                    const sent: Promise<{ status: number; body: unknown }> = runShopify
                        ? runShopify(String(entityId), sinceIso)
                        // Only for a process that has not loaded the functions.
                        : fetch(`${process.env.REBASE_DEV_URL || `http://localhost:${process.env.PORT || 3001}`}/api/functions/shopify-sync/${shopifyPath}/${entityId}${sinceIso ? `?since=${encodeURIComponent(sinceIso)}` : ""}`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                ...(process.env.REBASE_SERVICE_KEY ? { "Authorization": `Bearer ${process.env.REBASE_SERVICE_KEY}` } : {}),
                            },
                        }).then(async (res) => ({ status: res.status, body: await res.text().catch(() => "") }));
                    sent.then((result) => {
                        // 422 is "not linked to a Shopify customer": nothing to push.
                        if (result.status >= 400 && result.status !== 422) {
                            console.error(`[Shopify Sync] ${shopifyPath} for customer ${entityId} answered ${result.status}: ${JSON.stringify(result.body).slice(0, 300)}`);
                        }
                    }).catch((err) => {
                        // Log but don't throw — Shopify sync failure shouldn't block the save
                        console.error(`[Shopify Sync] ${shopifyPath} failed for customer ${entityId}:`, err);
                    });
                }
            }

            // ── Outbound Odoo Sync ──────────────────────────────
            // When the tier or the loyalty id changes, push the pricelist, tag
            // and barcode to Odoo. This runs as a fire-and-forget call so it
            // doesn't block the save callback if Odoo is slow or unreachable.
            //
            // A save that moves only the points — every purchase and return —
            // refreshes just the member summary on the contact (points, next
            // tier), which the `unfeigned_loyalty` module shows staff. That is
            // one small call, not a rewrite of the pricelist and tags.
            const odooPartnerId = values.odoo_partner_id;
            const odooSyncPath = (tierChanged || loyaltyIdChanged || offerChanged)
                ? "sync-loyalty"
                : (pointsChanged || statusChanged) ? "sync-summary" : null;
            if (odooSyncPath && odooPartnerId && process.env.ODOO_URL) {
                // In the same process, not over HTTP: every HTTP push counted
                // against the service key's rate limit (1,000 per 15 minutes),
                // and a burst of sales past it lost pushes. See IN_PROCESS_PUSH
                // in backend/functions/odoo-sync.ts, which publishes these.
                type Push = (customerId: string, since?: string) => Promise<{ status: number; body: unknown }>;
                const inProcess = (globalThis as Record<symbol, unknown>)[Symbol.for("unfeigned-loyalty.odoo-push")] as
                    Record<string, Push | undefined> | undefined;
                const runPush = inProcess?.[odooSyncPath];
                if (runPush) {
                    runPush(String(entityId), sinceIso).then((result) => {
                        if (result.status >= 400) {
                            console.error(`[Odoo Sync] ${odooSyncPath} for customer ${entityId} answered ${result.status}: ${JSON.stringify(result.body).slice(0, 300)}`);
                        }
                    }).catch((err) => {
                        // Log but don't throw — Odoo sync failure shouldn't block the save
                        console.error(`[Odoo Sync] Failed to push to Odoo for customer ${entityId}:`, err);
                    });
                    return;
                }

                // Only for a process that has not loaded the functions.
                const baseUrl = process.env.REBASE_DEV_URL || `http://localhost:${process.env.PORT || 3001}`;
                const serviceKey = process.env.REBASE_SERVICE_KEY;
                const since = sinceIso ? `?since=${encodeURIComponent(sinceIso)}` : "";

                // Retried when the middleware's own rate limit answers 429
                // (a burst of sales, a queue backlog after an outage): a
                // dropped push leaves the contact wrong until the daily load.
                const push = async (attempt: number): Promise<Response> => {
                    const res = await fetch(`${baseUrl}/api/functions/odoo-sync/${odooSyncPath}/${entityId}${since}`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            ...(serviceKey ? { "Authorization": `Bearer ${serviceKey}` } : {}),
                        },
                    });
                    if (res.status !== 429 || attempt >= 4) return res;
                    const retryAfter = Number(res.headers.get("retry-after"));
                    const wait = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : 5000 * 2 ** attempt;
                    await new Promise((resolve) => setTimeout(resolve, Math.min(wait, 120_000)));
                    return push(attempt + 1);
                };
                push(0).then(async (res) => {
                    // A refused or failed sync resolves the fetch just fine, so
                    // without this a missing service key (401) or an Odoo error
                    // (500) leaves the partner on the old tier with no trace.
                    if (!res.ok) {
                        console.error(`[Odoo Sync] ${odooSyncPath} for customer ${entityId} answered ${res.status}: ${(await res.text().catch(() => "")).slice(0, 300)}`);
                    }
                }).catch((err) => {
                    // Log but don't throw — Odoo sync failure shouldn't block the save
                    console.error(`[Odoo Sync] Failed to push pricelist for customer ${entityId}:`, err);
                });
            }
        }
    },
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid",
            admin: { readOnly: true }
        },
        odoo_partner_id: {
            name: "Odoo Partner ID",
            type: "number",
            validation: { unique: true, integer: true },
            description: "Odoo res.partner ID — source of truth for customer identity"
        },
        shopify_customer_id: {
            name: "Shopify Customer ID",
            type: "string",
            validation: { unique: true },
            description: "Shopify customer GID (e.g. gid://shopify/Customer/12345)"
        },
        email: {
            name: "Email",
            type: "string",
            validation: { required: true },
            email: true
        },
        first_name: {
            name: "First Name",
            type: "string",
            validation: { required: true }
        },
        last_name: {
            name: "Last Name",
            type: "string",
            validation: { required: true }
        },
        phone: {
            name: "Phone",
            type: "string"
        },
        lifetime_gross_spent: {
            name: "Lifetime Gross (€)",
            type: "number",
            defaultValue: 0,
            validation: { min: 0 },
            description: "Total gross amount spent across all channels",
            admin: { readOnly: true }
        },
        lifetime_returns: {
            name: "Lifetime Returns (€)",
            type: "number",
            defaultValue: 0,
            validation: { min: 0 },
            description: "Total amount returned/refunded",
            admin: { readOnly: true }
        },
        lifetime_net_points: {
            name: "Lifetime net (€)",
            type: "number",
            defaultValue: 0,
            description: "Gross − returns since the start, for reference. The level goes by the 12-month points.",
            admin: { readOnly: true }
        },
        points: {
            name: "Points (12 months)",
            type: "number",
            defaultValue: 0,
            description: "Points valid now: the last 12 months' purchases less their returns. 1€ = 1 point.",
            admin: { readOnly: true }
        },
        current_tier: {
            name: "Current Tier",
            type: "relation",
            description: "The member's level: reached on points and kept at least 12 months, or Family & Friends. " +
                "Set by the programme's rules on every save; give Family & Friends with the field below.",
            relation: {
                kind: "belongsTo",
                target: () => tiersCollection,
            }
        },
        manual_tier: {
            name: "Family & Friends",
            type: "relation",
            description: "A level given by hand (Family & Friends): it applies while set, whatever the points, and " +
                "never expires. Empty it to take it off; the member goes back to the level their points give.",
            relation: {
                kind: "belongsTo",
                target: () => tiersCollection,
            }
        },
        tier_reached_at: {
            name: "Level since",
            type: "date",
            mode: "date_time",
            admin: { readOnly: true, hideFromCollection: true }
        },
        tier_held_until: {
            name: "Level kept until",
            type: "date",
            mode: "date_time",
            description: "The level's 12 months end here; it is checked again then.",
            admin: { readOnly: true }
        },
        points_to_keep_level: {
            name: "Points to keep the level",
            type: "number",
            description: "How many more points, by the date above, keep the level another 12 months.",
            admin: { readOnly: true, hideFromCollection: true }
        },
        next_expiry_at: {
            name: "Next points expire",
            type: "date",
            mode: "date_time",
            admin: { readOnly: true, hideFromCollection: true }
        },
        next_expiry_points: {
            name: "Points expiring then",
            type: "number",
            admin: { readOnly: true, hideFromCollection: true }
        },
        offer_until: {
            name: "Second purchase −10% until",
            type: "date",
            mode: "date_time",
            description: "Open for 30 days after the first purchase, in place of INSIDER's discount.",
            admin: { readOnly: true, hideFromCollection: true }
        },
        first_purchase_at: {
            name: "First purchase",
            type: "date",
            mode: "date_time",
            admin: { readOnly: true, hideFromCollection: true }
        },
        purchases_count: {
            name: "Purchases",
            type: "number",
            admin: { readOnly: true, hideFromCollection: true }
        },
        review_at: {
            name: "Next review",
            type: "date",
            mode: "date_time",
            description: "When points expire, the level ends, the offer closes or the 30-day warning is due: the " +
                "daily review looks at the member again then.",
            admin: { readOnly: true, hideFromCollection: true }
        },
        level_warning_sent_for: {
            name: "Level warning sent for",
            type: "date",
            mode: "date_time",
            description: "The level end the 30-day warning was sent for, so it goes once.",
            admin: { readOnly: true, hideFromCollection: true }
        },
        evaluated_at: {
            name: "Rules applied at",
            type: "date",
            mode: "date_time",
            admin: { readOnly: true, hideFromCollection: true }
        },
        wallet_pass_serial: {
            name: "Wallet Pass Serial",
            type: "string",
            validation: { unique: true },
            description: "Loyalty id on the wallet card, scanned at POS (e.g. LOY-1234567)",
            admin: { readOnly: true }
        },
        wallet_pass_issued: {
            name: "Wallet Pass Issued",
            type: "boolean",
            defaultValue: false
        },
        is_active: {
            name: "Active",
            type: "boolean",
            defaultValue: true
        },
        notes: {
            name: "Notes",
            type: "string",
            admin: { multiline: true, hideFromCollection: true }
        },
        // ── Where this customer came from ─────────────────────────────
        //
        // Without this, a Platino in the table is unattributable: seeded test
        // data, a customer imported from ten years of Odoo history, someone who
        // registered this morning and an account auto-created by a purchase all
        // look identical, and the only way to tell them apart is to remember
        // which scripts were run against which environment and when. That
        // guesswork is how test records reach a real customer list.
        //
        // Written once, by whatever creates the row. Nothing recomputes it: a
        // customer who registered and later bought something is still a
        // registration — how they arrived does not change.
        origin: {
            name: "Origin",
            type: "string",
            description: "How this customer entered the programme.",
            enum: [
                { id: "registration", label: "Registered", color: "green" },
                { id: "odoo_enrolment", label: "Enrolled in Odoo", color: "purple" },
                { id: "odoo_purchase", label: "Auto-enrolled on a purchase", color: "cyan" },
                { id: "backfill", label: "Imported (backfill)", color: "orange" },
                { id: "seed", label: "Seeded (test data)", color: "red" },
                { id: "shopify", label: "Shopify", color: "blue" },
                { id: "admin", label: "Created in the admin", color: "gray" }
            ]
        },
        origin_detail: {
            name: "Origin detail",
            type: "map",
            keyValue: true,
            description:
                "What produced this row — the run id, the script and commit, the webhook and order that " +
                "enrolled them, the duplicate Odoo contacts merged into this account. Enough to find every " +
                "row a single import made, and to undo one without touching the rest.",
            admin: { hideFromCollection: true }
        },
        customer_since: {
            name: "Customer since",
            type: "date",
            mode: "date_time",
            description:
                "When they became a customer in Odoo, which is not when this row was made. A backfilled " +
                "customer of six years is not a new member, and tenure is the one thing an import would " +
                "otherwise destroy.",
            admin: { hideFromCollection: true }
        },
        created_at: {
            name: "Created At",
            type: "date",
            mode: "date_time",
            autoValue: "on_create",
            admin: { readOnly: true }
        },
        updated_at: {
            name: "Updated At",
            type: "date",
            mode: "date_time",
            autoValue: "on_update",
            admin: { readOnly: true, hideFromCollection: true }
        }
    },
    admin: {
        icon: "UserCircle",
        group: "Loyalty",
        defaultViewMode: "list",
        enabledViews: ["list", "table", "cards"],
        // Columns BESIDE the preview block, not including it. The preview below
        // already renders the name, so naming first_name/last_name here printed
        // the surname twice on every row.
        listProperties: ["current_tier", "points", "email"],
        // What a customer looks like when shown compactly — the list rows and
        // anywhere one is referenced from another record. Same reasoning: the
        // tier is the fact worth seeing without opening anything.
        previewProperties: ["first_name", "last_name"],
        openEntityMode: "split",
        // Left out of the list rows on purpose: four buttons per row is noise
        // in a list scrolled to find someone. `openEntityMode: "split"` puts the
        // customer beside the list on a click, and the actions are there — the
        // same two steps the lookup view took, without the second screen.
        entityActions: [
            {
                name: "Recalcular nivel",
                icon: "Calculator",
                onClick: async ({ entity, context, onCollectionChange }) => {
                    if (!entity || !context) return;
                    await invokeCustomerFunction({
                        name: "Recalcular nivel",
                        fn: "tier-engine",
                        path: id => `recalculate/${id}`,
                        done: "Nivel recalculado."
                    }, { id: String(entity.id), context, onCollectionChange });
                }
            },
            {
                name: "Emitir pase wallet",
                icon: "Wallet",
                onClick: async ({ entity, context, onCollectionChange }) => {
                    if (!entity || !context) return;
                    await invokeCustomerFunction({
                        name: "Emitir pase wallet",
                        fn: "wallet",
                        method: "GET",
                        path: id => `generate/${id}`,
                        done: "Pase wallet emitido."
                    }, { id: String(entity.id), context, onCollectionChange });
                }
            },
            // Family & Friends: one button to give it, one to take it off (back
            // to the level the rules give). An action's name is fixed, so it
            // cannot read one way or the other by customer; only the one that
            // applies is enabled, and the server refuses the other anyway.
            {
                name: "Dar Family & Friends",
                icon: "Heart",
                isEnabled: ({ entity }) => !entity?.values.manual_tier && entity?.values.is_active !== false,
                onClick: async ({ entity, context, onCollectionChange }) => {
                    if (!entity || !context) return;
                    await invokeCustomerFunction({
                        name: "Dar Family & Friends",
                        fn: "tier-engine",
                        path: id => `manual/${id}/assign`,
                        done: "Ahora es Family & Friends."
                    }, { id: String(entity.id), context, onCollectionChange });
                }
            },
            {
                name: "Quitar Family & Friends",
                icon: "HeartOff",
                isEnabled: ({ entity }) => Boolean(entity?.values.manual_tier),
                onClick: async ({ entity, context, onCollectionChange }) => {
                    if (!entity || !context) return;
                    await invokeCustomerFunction({
                        name: "Quitar Family & Friends",
                        fn: "tier-engine",
                        path: id => `manual/${id}/remove`,
                        done: "Ya no es Family & Friends: vuelve al nivel de sus puntos."
                    }, { id: String(entity.id), context, onCollectionChange });
                }
            },
            {
                name: "Reenviar a Odoo",
                icon: "RefreshCw",
                // Greyed out rather than hidden: a customer with no Odoo id is a
                // fact worth seeing, and an action that silently disappears
                // reads as a bug.
                isEnabled: ({ entity }) => Boolean(entity?.values.odoo_partner_id),
                onClick: async ({ entity, context, onCollectionChange }) => {
                    if (!entity || !context) return;
                    await invokeCustomerFunction({
                        name: "Reenviar a Odoo",
                        fn: "odoo-sync",
                        path: id => `sync-loyalty/${id}`,
                        done: "Reenviado a Odoo."
                    }, { id: String(entity.id), context, onCollectionChange });
                }
            },
            {
                name: "Reenviar a Shopify",
                icon: "Store",
                isEnabled: ({ entity }) => Boolean(entity?.values.shopify_customer_id),
                onClick: async ({ entity, context, onCollectionChange }) => {
                    if (!entity || !context) return;
                    await invokeCustomerFunction({
                        name: "Reenviar a Shopify",
                        fn: "shopify-sync",
                        path: id => `sync-tags/${id}`,
                        done: "Reenviado a Shopify."
                    }, { id: String(entity.id), context, onCollectionChange });
                }
            }
        ],
        exportable: true,
        selectionEnabled: true,
        sort: ["created_at", "desc"],
        propertiesOrder: [
            "first_name", "last_name", "email", "phone",
            "current_tier", "manual_tier", "points", "tier_held_until", "points_to_keep_level",
            "next_expiry_at", "next_expiry_points", "offer_until",
            "lifetime_net_points", "lifetime_gross_spent", "lifetime_returns",
            "tier_reached_at", "first_purchase_at", "purchases_count", "review_at", "level_warning_sent_for", "evaluated_at",
            "wallet_pass_serial", "wallet_pass_issued", "is_active",
            "odoo_partner_id", "shopify_customer_id",
            "origin", "origin_detail", "customer_since",
            "notes", "created_at", "updated_at"
        ]
    }
});

export default customersCollection;
